import { useState, useEffect } from 'react'
import { Globe, ArrowRight, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'

interface BuilderPhaseUrlProps {
  websiteUrl: string
  scrapeStatus: string
  preloaded: boolean
  onAnalyze: (url: string) => void
  onSkip: () => void
  onContinueWithExisting: () => void
}

export default function BuilderPhaseUrl({
  websiteUrl,
  scrapeStatus,
  preloaded,
  onAnalyze,
  onSkip,
  onContinueWithExisting,
}: BuilderPhaseUrlProps) {
  const [url, setUrl] = useState(websiteUrl)

  // Sync when websiteUrl loads asynchronously
  useEffect(() => {
    if (websiteUrl && !url) setUrl(websiteUrl)
  }, [websiteUrl])

  const handleAnalyze = () => {
    let normalized = url.trim()
    if (normalized && !normalized.startsWith('http')) {
      normalized = 'https://' + normalized
    }
    if (!normalized) return
    onAnalyze(normalized)
  }

  if (preloaded) {
    return (
      <div className="space-y-4">
        <div className="p-4 rounded-lg border bg-green-50 border-green-200">
          <p className="text-sm font-medium text-green-800">We already have info from your website</p>
          <p className="text-xs text-green-700 mt-1">You can continue with this data, edit the URL, or re-analyze a different site.</p>
        </div>
        <div className="flex gap-2">
          <Input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="www.yourfirm.com"
            className="flex-1"
          />
          <Button variant="outline" onClick={handleAnalyze} disabled={!url.trim()}>
            Re-analyze
          </Button>
        </div>
        <Button className="w-full" onClick={onContinueWithExisting}>
          Continue with current data
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="text-center space-y-2">
        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-brand-indigo/10 to-brand-royal/10 flex items-center justify-center mx-auto">
          <Globe className="h-7 w-7 text-brand-royal" />
        </div>
        <h2 className="text-xl font-semibold">Let's build your AI team</h2>
        <p className="text-sm text-muted-foreground max-w-sm mx-auto">
          Paste your website URL and we'll learn about your firm to build the perfect inbound and outbound agents.
        </p>
      </div>

      <div className="space-y-3">
        <div className="flex gap-2">
          <Input
            placeholder="www.yourfirm.com"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleAnalyze()}
            className="flex-1"
            disabled={scrapeStatus === 'scraping'}
          />
          <Button onClick={handleAnalyze} disabled={!url.trim() || scrapeStatus === 'scraping'}>
            {scrapeStatus === 'scraping' ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>Analyze</>
            )}
          </Button>
        </div>
        <button
          className="text-xs text-muted-foreground hover:text-foreground transition-colors w-full text-center"
          onClick={onSkip}
        >
          Skip — I'll enter details manually
        </button>
      </div>
    </div>
  )
}
